import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";

const AUDIENCES = [
  { value: "all", label: "Everyone" },
  { value: "student", label: "Students" },
  { value: "parent", label: "Parents" },
  { value: "tutor", label: "Tutors" },
  { value: "admin", label: "Admins & staff" },
];
const audienceLabel = (v) => AUDIENCES.find((a) => a.value === v)?.label || v;

const EMPTY = { audience: "all", title: "", body: "", is_active: true, sort: 0 };

/**
 * FAQ / knowledge the student Study Coach and parent Progress Assistant answer from.
 * Active articles for the matching audience are added to the assistant's prompt.
 */
export default function AssistantKnowledgeBase() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("admin/assistant-knowledge", { params: filter ? { audience: filter } : {} });
      setRows(res.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not load articles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, sort: Number(editing.sort) || 0 };
      if (editing.id) await api.put(`admin/assistant-knowledge/${editing.id}`, payload);
      else await api.post("admin/assistant-knowledge", payload);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (e) {
      const errors = e.response?.data?.errors;
      toast.error(errors ? Object.values(errors).flat()[0] : e.response?.data?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`admin/assistant-knowledge/${row.id}`, { ...row, is_active: !row.is_active });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not update.");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await api.delete(`admin/assistant-knowledge/${row.id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not delete.");
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Assistant Knowledge Base
          </Typography>
          <Typography variant="body2" color="text.secondary">
            FAQs and guidance the student Study Coach, parent Progress Assistant, tutor Teaching Assistant and staff Admin Assistant use when answering.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ ...EMPTY })}>
          Add article
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each child's progress (scores, weak topics, readiness) is added automatically. Use this page for things the
        assistant cannot know from data: exam dates, policies, how features work, study tips. Keep articles short.
        Changes apply to the next message.
      </Alert>

      <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v !== null && setFilter(v)} sx={{ mb: 2 }}>
        <ToggleButton value="">All</ToggleButton>
        {AUDIENCES.map((a) => (
          <ToggleButton key={a.value} value={a.value}>
            {a.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        {loading ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 3 }} color="text.secondary">
            No articles yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>Order</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.sort}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{row.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 520 }} noWrap>
                      {row.body}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={audienceLabel(row.audience)} />
                  </TableCell>
                  <TableCell>
                    <Switch size="small" checked={!!row.is_active} onChange={() => toggleActive(row)} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing({ ...row })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => remove(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="md">
        <DialogTitle>{editing?.id ? "Edit article" : "New article"}</DialogTitle>
        {editing && (
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Title"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                inputProps={{ maxLength: 190 }}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="Shown to"
                  value={editing.audience}
                  onChange={(e) => setEditing({ ...editing, audience: e.target.value })}
                  sx={{ minWidth: 220 }}
                >
                  {AUDIENCES.map((a) => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Order"
                  type="number"
                  value={editing.sort}
                  onChange={(e) => setEditing({ ...editing, sort: e.target.value })}
                  sx={{ width: 120 }}
                />
                <FormControlLabel
                  control={
                    <Switch checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                  }
                  label="Active"
                />
              </Stack>
              <TextField
                label="Content"
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                multiline
                minRows={8}
                inputProps={{ maxLength: 8000 }}
                helperText={`${editing.body.length}/8000 — plain text; you can use [link text](/student/mock-exams) links.`}
                fullWidth
              />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || !editing?.title?.trim() || !editing?.body?.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
